import { Calendar } from "primereact/calendar";
import { useState, useEffect } from "react";
import { getPrimeCalendarConfig } from "../../../common/primeCalendarSetup";
import { useActionRule } from "../../hooks/use-actionRuleDate";
import { useForeignShopRule } from "../../hooks/use-foreignShopRuleData";
import { useProductRule } from "../../hooks/use-productRuleData";
import { useOptions } from "../../hooks/use-optionsData";
import PPDecimalInput from "../../../common/PPDecimalInput";
import { PPDefaultSelector } from "../../../../components/ui/PPDefaultSelector";
import Select from "react-select";
import RuleEngineBackend from "../../../services/RuleEngineBackend";
import PPUtils from "../../../../utils/PPUtils";
import PPPriceManagementConditionModal from "../PPPriceManagementExtendedPriceChangeModal";
import { parseDecimalInputOrNull } from "../../../common/decimalInputConfig";
import { FormulaDecimalInput, selectedOption, finiteNumberOrZero, convertToServerOperator, formulaRowKey, computeNextExecutionDate } from "./actionRuleHelpers";
import {
     createEmptyPriceAdjustmentDraft,
     isPriceAdjustmentRuleConfigured,
     resolvePriceAdjustmentModalRuleId,
} from "../../utils/priceAdjustmentModal";

export function PPPriceManagementActionRuleTile({selectedRule, editingRule, setSaveAction, setCanSave}) {
     const { PriceChangeMarginHelper, PriceChangeHelper } = useActionRule();
     const { StockHelper } = useProductRule();
     const stockRuleCount = StockHelper.getStockRules().length;
     const priceChangeMaxRules = stockRuleCount > 0 ? -1 : 5;
     const selectedRuleId = selectedRule?.id;
     const hasSelectedRule = selectedRuleId !== undefined && selectedRuleId !== null;
     const isEditingPriceChange = editingRule?.keyString === "priceChange";
     const isEditingMargin = editingRule?.keyString === "priceChangeMargin";

     const hasPriceChangeComponent =
          PriceChangeHelper.hasActivePriceAdjustmentRules() || isEditingPriceChange;
     const hasMarginComponent =
          PriceChangeMarginHelper.isActive() || PriceChangeMarginHelper.isConfigured() || isEditingMargin;

     const isPriceChangeBlockedByMargin =
          selectedRuleId === 1 && hasMarginComponent && !isEditingPriceChange;
     const isMarginBlockedByPriceChange =
          selectedRuleId === 4 && hasPriceChangeComponent && !isEditingMargin;

     useEffect(() => {
          if (!hasSelectedRule) {
               setSaveAction(null);
               setCanSave(false);
               return;
          }

          if (isPriceChangeBlockedByMargin || isMarginBlockedByPriceChange) {
               setSaveAction(null);
               setCanSave(false);
          }
     }, [hasSelectedRule, isPriceChangeBlockedByMargin, isMarginBlockedByPriceChange, setCanSave, setSaveAction]);

     if (!hasSelectedRule) {
          return null;
     }

     if (isPriceChangeBlockedByMargin) {
          return <div className="alert alert-danger m-4" role="alert">
               <p>Preisangleichung kann nicht verwendet werden, da bereits eine Preisanpassung nach Marge Komponente existiert.</p>
               <small>Deaktivieren Sie zuerst die Preisanpassung nach Marge, um Preisangleichung zu verwenden.</small>
          </div>;
     }

     if (isMarginBlockedByPriceChange) {
          return <div className="alert alert-danger m-4" role="alert">
               <p>Preisanpassung nach Marge kann nicht verwendet werden, da bereits eine Preisangleichung Komponente existiert.</p>
               <small>Deaktivieren Sie zuerst die Preisangleichung, um Preisanpassung nach Marge zu verwenden.</small>
          </div>;
     }

     switch (selectedRuleId) {
          case 0:
               return <PPMaxPriceChangeModal setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 1:
               return <PPPriceChangeModal maxRules={priceChangeMaxRules} editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 2:
               return <PPPriceChangeIntervalModal setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 3:
               return <PPPriceRoundingModal setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 4:
               return <PPPriceChangeViaMarginModal editingRule={editingRule} setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 5:
               return <PPChannelRuleCalculationModal setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          case 6:
               return <PPAmazonBuyBoxModal setSaveAction={setSaveAction} setCanSave={setCanSave} />;
          default:
               return "";
     }
}

function PPMaxPriceChangeModal({setSaveAction, setCanSave}) {
     const { MaxPriceChangeHelper } = useActionRule();
     const { getActionRuleOptions } = useOptions();
     
     useEffect(() => {
          setSaveAction(() => () => {
               MaxPriceChangeHelper.toggle(true);
          });

          setCanSave(MaxPriceChangeHelper.isConfigured());
     }, [setSaveAction, MaxPriceChangeHelper, setCanSave]);
     
     const actionRuleOptions = getActionRuleOptions();
     const numericKeysOptions = actionRuleOptions.productAttributeNumericKeys || []
     const isTooler = PPUtils.getCustomerName() === "Tooler";

     return (
          <div className="pt-3 pp-form-stack">
               <div className="ps-2 pp-form-inline">
                    <span className="text-nowrap">Maximale Preisminderung:</span>
                    <PPDecimalInput
                         value={Number.isFinite(MaxPriceChangeHelper.getMaxPriceDecreaseValue()) ?
                              MaxPriceChangeHelper.getMaxPriceDecreaseValue() : 0}
                         onValueChange={(v) => MaxPriceChangeHelper.setMaxPriceDecreaseValue(v)}
                         className={`form-control`}
                         decimalsLimit={2}
                         suffix={"%"}
                         step={1}
                         maxLength={6}
                         defaultValue={0}
                    />
               </div>

               <div className="ps-2 pp-form-inline">
                    <span className="text-nowrap">Maximale Preissteigerung:</span>
                    <PPDecimalInput
                         value={Number.isFinite(MaxPriceChangeHelper.getMaxPriceIncreaseValue()) ?
                              MaxPriceChangeHelper.getMaxPriceIncreaseValue() : 0}
                         onValueChange={(v) => MaxPriceChangeHelper.setMaxPriceIncreaseValue(v)}
                         className={`form-control`}
                         decimalsLimit={2}
                         suffix={"%"}
                         step={1}
                         maxLength={6}
                         defaultValue={0}
                    />
               </div>

               <div className="ps-2 text-muted small">
                    <p className="mb-1">Der EK ist immer ohne Mehrwertsteuer und</p>
                    <p className="mb-0">Die Formeln werden von links nach rechts berechnet - kein Punkt vor Strich!</p>
               </div>

               <div className="ps-2">
                    <label className="form-check form-switch form-switch-inline">
                         <input 
                              className="form-check-input" 
                              type="checkbox" 
                              role="switch"
                              checked={MaxPriceChangeHelper.getMinFormulaAddTax()}
                              onChange={() => MaxPriceChangeHelper.setMinFormulaAddTax(
                                   !MaxPriceChangeHelper.getMinFormulaAddTax()
                              )}     
                         />
                         <span className="form-check-label text-md ms-1">Mehrwertsteuer nach der Berechnung des Mindestpreises aufschlagen</span>
                    </label>
               </div>

               <div className="ps-2 pp-form-inline">
                    <span className="text-nowrap">Mindestpreis =</span>
                    
                    <PPDefaultSelector 
                         id="selectMinBase"
                         data={numericKeysOptions}
                         onChange={(e) => MaxPriceChangeHelper.setMinFormulaBase(e?.value || "")}
                         name="Min Formula Base"
                         value={selectedOption(numericKeysOptions, MaxPriceChangeHelper.getMinFormulaBase())}
                    />

                    <PPDefaultSelector 
                         id="selectMinOP1"
                         data={[
                              {
                                  value: "+",
                                  label: "+"
                              },
                              {
                                  value: "-",
                                  label: "-"
                              },
                              {
                                  value: "*",
                                  label: "*"
                              },
                              {
                                  value: "/",
                                  label: "/"
                              }
                         ]}
                         onChange={(e) => {
                              const currentMin = MaxPriceChangeHelper.getMinimumPrice();
                              MaxPriceChangeHelper.setMinimumPrice({
                                   ...currentMin,
                                   OP_1: e?.value || null
                              });
                         }}
                         name="Min Formula OP1"
                         value={MaxPriceChangeHelper.getMinimumPrice()?.OP_1 ? {
                              value: MaxPriceChangeHelper.getMinimumPrice().OP_1,
                              label: MaxPriceChangeHelper.getMinimumPrice().OP_1
                         } : null}
                    />

                    <FormulaDecimalInput
                         value={MaxPriceChangeHelper.getMinimumPrice().X}
                         onChange={(nextValue) => {
                              const currentMin = MaxPriceChangeHelper.getMinimumPrice();
                              MaxPriceChangeHelper.setMinimumPrice({
                                   ...currentMin,
                                   X: nextValue,
                              });
                         }}
                         suffix="€"
                    />

                    <PPDefaultSelector 
                         id="selectMinOP2"
                         data={[
                              {
                                  value: "+",
                                  label: "+"
                              },
                              {
                                  value: "-",
                                  label: "-"
                              }
                         ]}
                         onChange={(e) => {
                              const currentMin = MaxPriceChangeHelper.getMinimumPrice();
                              MaxPriceChangeHelper.setMinimumPrice({
                                   ...currentMin,
                                   OP_2: e?.value || null
                              });
                         }}
                         name="Min Formula OP2"
                         value={MaxPriceChangeHelper.getMinimumPrice()?.OP_2 ? {
                              value: MaxPriceChangeHelper.getMinimumPrice().OP_2,
                              label: MaxPriceChangeHelper.getMinimumPrice().OP_2
                         } : null}
                    />

                    <FormulaDecimalInput
                         value={MaxPriceChangeHelper.getMinimumPrice().Y}
                         onChange={(nextValue) => {
                              const currentMin = MaxPriceChangeHelper.getMinimumPrice();
                              MaxPriceChangeHelper.setMinimumPrice({
                                   ...currentMin,
                                   Y: nextValue,
                              });
                         }}
                         suffix="%"
                    />

                    <PPDefaultSelector 
                         id="selectMinOP3"
                         data={[
                              {
                                  value: "+",
                                  label: "+"
                              },
                              {
                                  value: "-",
                                  label: "-"
                              },
                              {
                                  value: "*",
                                  label: "*"
                              },
                              {
                                  value: "/",
                                  label: "/"
                              }
                         ]}
                         onChange={(e) => {
                              const currentMin = MaxPriceChangeHelper.getMinimumPrice();
                              MaxPriceChangeHelper.setMinimumPrice({
                                   ...currentMin,
                                   OP_3: e?.value || null
                              });
                         }}
                         name="Min Formula OP3"
                         value={MaxPriceChangeHelper.getMinimumPrice()?.OP_3 ? {
                              value: MaxPriceChangeHelper.getMinimumPrice().OP_3,
                              label: MaxPriceChangeHelper.getMinimumPrice().OP_3
                         } : null}
                    />

                    <FormulaDecimalInput
                         value={MaxPriceChangeHelper.getMinimumPrice().Z}
                         onChange={(nextValue) => {
                              const currentMin = MaxPriceChangeHelper.getMinimumPrice();
                              MaxPriceChangeHelper.setMinimumPrice({
                                   ...currentMin,
                                   Z: nextValue,
                              });
                         }}
                         suffix="€"
                    />
               </div>

               {isTooler && (
                    <div className="ps-2">
                         <div className="form-check">
                              <input
                                   className="form-check-input"
                                   type="checkbox"
                                   id="max_price_minimum_use_feed_factor"
                                   checked={!!MaxPriceChangeHelper.getMinimumPrice()?.useFactor}
                                   onChange={(e) => MaxPriceChangeHelper.setMinimumPrice({
                                        ...MaxPriceChangeHelper.getMinimumPrice(),
                                        useFactor: e.target.checked
                                   })}
                              />
                              <label className="form-check-label" htmlFor="max_price_minimum_use_feed_factor">
                                   × Feed Faktor
                              </label>
                         </div>
                    </div>
               )}

               <div className="ps-2">
                    <div className="form-check form-switch form-switch-inline mb-2">
                         <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="max_price_minimum_boundary_enabled"
                              checked={MaxPriceChangeHelper.isMinimumBoundaryEnabled()}
                              onChange={() => MaxPriceChangeHelper.setMinimumBoundaryEnabled(
                                   !MaxPriceChangeHelper.isMinimumBoundaryEnabled()
                              )}
                         />
                         <label className="form-check-label text-md ms-1" htmlFor="max_price_minimum_boundary_enabled">
                              Aber mindestens aktivieren
                         </label>
                    </div>

                    <div
                         className={`pp-form-inline ${MaxPriceChangeHelper.isMinimumBoundaryEnabled() ? "" : "opacity-50"}`}
                    >
                         <span className="text-nowrap">Aber mindestens: ( Einkaufspreis +</span>
                    
                    <PPDecimalInput
                         value={MaxPriceChangeHelper.getMinimumEuroOverEK() ?? 0}
                         onValueChange={(v) => MaxPriceChangeHelper.setMinimumEuroOverEK(v)}
                         className={`form-control`}
                         decimalsLimit={2}
                         suffix={"€"}
                         step={1}
                         maxLength={6}
                         defaultValue={0}
                         disabled={!MaxPriceChangeHelper.isMinimumBoundaryEnabled()}
                    />
                    
                    <span className="text-nowrap">) + MwSt. +</span>
                    
                    <PPDecimalInput
                         value={MaxPriceChangeHelper.getMinimumPercent() ?? 0}
                         onValueChange={(v) => MaxPriceChangeHelper.setMinimumPercent(v)}
                         className={`form-control`}
                         decimalsLimit={2}
                         suffix={"%"}
                         step={1}
                         maxLength={6}
                         defaultValue={0}
                         disabled={!MaxPriceChangeHelper.isMinimumBoundaryEnabled()}
                    />
                    </div>
               </div>

               <div className="ps-2">
                    <label className="form-check form-switch form-switch-inline">
                         <input 
                              className="form-check-input" 
                              type="checkbox" 
                              role="switch"
                              checked={MaxPriceChangeHelper.getMaxFormulaAddTax()}
                              onChange={() => MaxPriceChangeHelper.setMaxFormulaAddTax(
                                   !MaxPriceChangeHelper.getMaxFormulaAddTax()
                              )}     
                         />
                         <span className="form-check-label text-md ms-1">Mehrwertsteuer nach der Berechnung des Maximalpreises aufschlagen</span>
                    </label>
               </div>

               <div className="ps-2 pp-form-inline">
                    <span className="text-nowrap">Maximalpreis =</span>
                    
                    <PPDefaultSelector 
                         id="selectMaxBase"
                         data={numericKeysOptions}
                         onChange={(e) => MaxPriceChangeHelper.setMaxFormulaBase(e?.value || "")}
                         name="Max Formula Base"
                         value={selectedOption(numericKeysOptions, MaxPriceChangeHelper.getMaxFormulaBase())}
                    />

                    <PPDefaultSelector 
                         id="selectMaxOP1"
                         data={[
                              {
                                  value: "+",
                                  label: "+"
                              },
                              {
                                  value: "-",
                                  label: "-"
                              },
                              {
                                  value: "*",
                                  label: "*"
                              },
                              {
                                  value: "/",
                                  label: "/"
                              }
                         ]}
                         onChange={(e) => {
                              const currentMax = MaxPriceChangeHelper.getMaximumPrice();
                              MaxPriceChangeHelper.setMaximumPrice({
                                   ...currentMax,
                                   OP_1: e?.value || null
                              });
                         }}
                         name="Max Formula OP1"
                         value={MaxPriceChangeHelper.getMaximumPrice()?.OP_1 ? {
                              value: MaxPriceChangeHelper.getMaximumPrice().OP_1,
                              label: MaxPriceChangeHelper.getMaximumPrice().OP_1
                         } : null}
                    />

                    <FormulaDecimalInput
                         value={MaxPriceChangeHelper.getMaximumPrice().X}
                         onChange={(nextValue) => {
                              const currentMax = MaxPriceChangeHelper.getMaximumPrice();
                              MaxPriceChangeHelper.setMaximumPrice({
                                   ...currentMax,
                                   X: nextValue,
                              });
                         }}
                         suffix="€"
                    />

                    <PPDefaultSelector 
                         id="selectMaxOP2"
                         data={[
                              {
                                  value: "+",
                                  label: "+"
                              },
                              {
                                  value: "-",
                                  label: "-"
                              }
                         ]}
                         onChange={(e) => {
                              const currentMax = MaxPriceChangeHelper.getMaximumPrice();
                              MaxPriceChangeHelper.setMaximumPrice({
                                   ...currentMax,
                                   OP_2: e?.value || null
                              });
                         }}
                         name="Max Formula OP2"
                         value={MaxPriceChangeHelper.getMaximumPrice()?.OP_2 ? {
                              value: MaxPriceChangeHelper.getMaximumPrice().OP_2,
                              label: MaxPriceChangeHelper.getMaximumPrice().OP_2
                         } : null}
                    />

                    <FormulaDecimalInput
                         value={MaxPriceChangeHelper.getMaximumPrice().Y}
                         onChange={(nextValue) => {
                              const currentMax = MaxPriceChangeHelper.getMaximumPrice();
                              MaxPriceChangeHelper.setMaximumPrice({
                                   ...currentMax,
                                   Y: nextValue,
                              });
                         }}
                         suffix="%"
                    />

                    <PPDefaultSelector 
                         id="selectMaxOP3"
                         data={[
                              {
                                  value: "+",
                                  label: "+"
                              },
                              {
                                  value: "-",
                                  label: "-"
                              },
                              {
                                  value: "*",
                                  label: "*"
                              },
                              {
                                  value: "/",
                                  label: "/"
                              }
                         ]}
                         onChange={(e) => {
                              const currentMax = MaxPriceChangeHelper.getMaximumPrice();
                              MaxPriceChangeHelper.setMaximumPrice({
                                   ...currentMax,
                                   OP_3: e?.value || null
                              });
                         }}
                         name="Max Formula OP3"
                         value={MaxPriceChangeHelper.getMaximumPrice()?.OP_3 ? {
                              value: MaxPriceChangeHelper.getMaximumPrice().OP_3,
                              label: MaxPriceChangeHelper.getMaximumPrice().OP_3
                         } : null}
                    />

                    <FormulaDecimalInput
                         value={MaxPriceChangeHelper.getMaximumPrice().Z}
                         onChange={(nextValue) => {
                              const currentMax = MaxPriceChangeHelper.getMaximumPrice();
                              MaxPriceChangeHelper.setMaximumPrice({
                                   ...currentMax,
                                   Z: nextValue,
                              });
                         }}
                         suffix="€"
                    />
               </div>

               {isTooler && (
                    <div className="ps-2">
                         <div className="form-check">
                              <input
                                   className="form-check-input"
                                   type="checkbox"
                                   id="max_price_maximum_use_feed_factor"
                                   checked={!!MaxPriceChangeHelper.getMaximumPrice()?.useFactor}
                                   onChange={(e) => MaxPriceChangeHelper.setMaximumPrice({
                                        ...MaxPriceChangeHelper.getMaximumPrice(),
                                        useFactor: e.target.checked
                                   })}
                              />
                              <label className="form-check-label" htmlFor="max_price_maximum_use_feed_factor">
                                   × Feed Faktor
                              </label>
                         </div>
                    </div>
               )}
          </div>
     );
}

function PPPriceChangeModal({ maxRules = -1, editingRule, setSaveAction, setCanSave }) {
     const { PriceChangeHelper } = useActionRule();
     const { ForeignShopStockHelper } = useForeignShopRule();
     
     const existingRules = PriceChangeHelper.getPriceAdjustmentRules();
     const isEditing = editingRule && editingRule.keyString === 'priceChange';
     const sessionDraftId = existingRules.find((rule) => !rule.active)?.id ?? null;
     const activeRuleId = resolvePriceAdjustmentModalRuleId({
          isEditing,
          editingRuleId: editingRule?.id,
          existingRules,
          sessionDraftId,
     });
     const activeRule = activeRuleId
          ? PriceChangeHelper.getPriceAdjustmentRule(activeRuleId)
          : null;
     const foreignShopStockRules = ForeignShopStockHelper.getForeignShopStockRules();

     const pc = {
          getPriceAdjustmentRules: PriceChangeHelper.getPriceAdjustmentRules,
          getPriceAdjustmentRule: PriceChangeHelper.getPriceAdjustmentRule,
          addPriceAdjustmentRule: PriceChangeHelper.addPriceAdjustmentRule,
          updatePriceAdjustmentRule: PriceChangeHelper.updatePriceAdjustmentRule,
          isConfigured: PriceChangeHelper.isConfigured,
          getAction: () => activeRule?.action || [],
          setAction: (val) => activeRuleId && PriceChangeHelper.updatePriceAdjustmentRule(activeRuleId, "action", val),
          getComparisonOption: () => activeRule?.ComparisonOption ?? null,
          setComparisonOption: (val) => activeRuleId && PriceChangeHelper.updatePriceAdjustmentRule(activeRuleId, "ComparisonOption", val),
          getTypeOfPriceChangeInEuro: () => activeRule?.typeOfPriceChangeInEuro || false,
          setTypeOfPriceChangeInEuro: (val) => activeRuleId && PriceChangeHelper.updatePriceAdjustmentRule(activeRuleId, "typeOfPriceChangeInEuro", val),
          getPriceChangeAsCoupon: () => activeRule?.priceChangeAsCoupon || false,
          setPriceChangeAsCoupon: (val) => activeRuleId && PriceChangeHelper.updatePriceAdjustmentRule(activeRuleId, "priceChangeAsCoupon", val),
     };

     const [extendedPriceChange, setExtendedPriceChange] = useState(() =>
          Array.isArray(activeRule?.action) && activeRule.action.some((f) => f?.priceType != null)
     );
     const [conditionModalVisible, setConditionModalVisible] = useState(false);
     const [currentEditingFormulaIndex, setCurrentEditingFormulaIndex] = useState(null);

     useEffect(() => {
          if (activeRuleId && PriceChangeHelper.setActivePriceAdjustmentRuleId) {
               PriceChangeHelper.setActivePriceAdjustmentRuleId(activeRuleId);
          }
     }, [activeRuleId, PriceChangeHelper]);

     useEffect(() => {
          if (activeRule) {
               setExtendedPriceChange(
                    Array.isArray(activeRule.action) && activeRule.action.some((f) => f?.priceType != null)
               );
          }
     }, [activeRule?.id, activeRule?.action]);

     const activePriceAdjustmentCount = existingRules.filter((rule) => rule.active).length;
     const wouldExceedMaxRules = maxRules > 0 && !isEditing && activePriceAdjustmentCount >= maxRules;

     useEffect(() => {
          if (isEditing || wouldExceedMaxRules || activeRuleId) {
               return;
          }
          PriceChangeHelper.addPriceAdjustmentRule(createEmptyPriceAdjustmentDraft(extendedPriceChange));
     }, [activeRuleId, isEditing, wouldExceedMaxRules, PriceChangeHelper, extendedPriceChange]);

     useEffect(() => {
          if (activeRuleId && activeRule && activeRule.action.length === 0) {
               PriceChangeHelper.updatePriceAdjustmentRule(
                    activeRuleId,
                    "action",
                    extendedPriceChange
                         ? [{ priceType: null, underBidAmount: null, condition: null }]
                         : [{ priceUntil: null, underBidAmount: null }]
               );
          }
     }, [activeRuleId, activeRule, PriceChangeHelper, extendedPriceChange]);

     useEffect(() => {
          setSaveAction(() => () => {
               let targetId = activeRuleId;
               if (!targetId) {
                    targetId = PriceChangeHelper.addPriceAdjustmentRule({
                         ...createEmptyPriceAdjustmentDraft(extendedPriceChange),
                         active: true,
                    });
               }
               PriceChangeHelper.updatePriceAdjustmentRule(targetId, "active", true);
          });

          setCanSave(isPriceAdjustmentRuleConfigured(activeRule));
     }, [setSaveAction, PriceChangeHelper, setCanSave, activeRule, activeRuleId, extendedPriceChange]);
     
     const handleConditionModalOpen = (formulaIndex) => {
          setCurrentEditingFormulaIndex(formulaIndex);
          setConditionModalVisible(true);
     };

     const handleConditionSave = (condition) => {
          const formulas = pc.getAction();
          const newFormulas = [...formulas];
          newFormulas[currentEditingFormulaIndex] = { 
               ...newFormulas[currentEditingFormulaIndex], 
               condition: condition 
          };
          pc.setAction(newFormulas);
          setConditionModalVisible(false);
          setCurrentEditingFormulaIndex(null);
     };

     const getCurrentCondition = () => {
          if (currentEditingFormulaIndex === null) return null;
          const formulas = pc.getAction();
          return formulas[currentEditingFormulaIndex]?.condition || null;
     };
     
     if (wouldExceedMaxRules) {
          return <div className="alert alert-warning m-4" role="alert">
               <p>Maximale Anzahl von Preisangleichungs-Regeln erreicht!</p>
               <small>Sie können maximal {maxRules} Regel(n) erstellen, wenn keine Verfügbarkeits-Komponenten vorhanden sind.</small>
          </div>;
     }

     return (
          <div className="pt-3 pp-form-stack">
               <div className="ps-2">

                    <div className=" d-flex flex-row justify-content-between align-items-center">
                         <div className="mb-3 form-check mb-0">
                              <input 
                                   type="checkbox" 
                                   className="form-check-input" 
                                   id="stock_red"
                                   checked={pc.getPriceChangeAsCoupon()}
                                   onChange={(event) => pc.setPriceChangeAsCoupon(event.target.checked)}
                              />
                              <label className="form-check-label" htmlFor="stock_red">Preisangleichung als Gutschein auswerten</label>
                         </div>
                         <button 
                              type="button" 
                              className="btn btn-outline-primary"
                              onClick={() => {
                                   const newExtendedMode = !extendedPriceChange;
                                   setExtendedPriceChange(newExtendedMode);
                                   
                                   if (newExtendedMode) {
                                        pc.setAction([{ priceType: null, underBidAmount: null, condition: null }]);
                                   } else {
                                        pc.setAction([{ priceUntil: null, underBidAmount: null }]);
                                   }
                              }}
                         >
                              {extendedPriceChange ? "Einfache Preisangleichung" : "Erweiterte Preisangleichung"}
                         </button>
                    </div>

                    <div className="mb-4">
                         <h6 className="mb-3">Art der Preisangleichung</h6>

                         <div className="mb-3">
                              <div className="form-check">
                                   <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="priceChangeType" 
                                        id="priceChangePercent"
                                        checked={!pc.getTypeOfPriceChangeInEuro()}
                                        onChange={() => pc.setTypeOfPriceChangeInEuro(false)}
                                   />
                                   <label className="form-check-label" htmlFor="priceChangePercent">
                                        Prozentuale Preisangleichung (%)
                                   </label>
                              </div>
                              <div className="form-check">
                                   <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="priceChangeType" 
                                        id="priceChangeEuro"
                                        checked={pc.getTypeOfPriceChangeInEuro()}
                                        onChange={() => pc.setTypeOfPriceChangeInEuro(true)}
                                   />
                                   <label className="form-check-label" htmlFor="priceChangeEuro">
                                        Preisangleichung in Euro (€)
                                   </label>
                              </div>
                         </div>
                    </div>

                    {extendedPriceChange ? (
                         <div className="mb-4">
                              <h6 className="mb-3">Erweiterte Preisformeln:</h6>
                              <div className="bg-light p-3 rounded">
                                   <p className="mb-3">
                                        <strong>Erweiterte Preisangleichung</strong>
                                   </p>
                                   
                                   {pc.getAction().map((formula, index) => (
                                        <div key={formulaRowKey(formula)} className="mb-3 p-3 border rounded">
                                             <div className="row align-items-center">
                                                  <div className="col-auto">
                                                       <strong>Wenn</strong>
                                                  </div>
                                                  <div className="col-auto">
                                                       <button 
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => handleConditionModalOpen(index)}
                                                       >
                                                            <i className="fas fa-plus me-1"></i>{' '}Bedingung hinzufügen
                                                       </button>
                                                  </div>
                                                  <div className="col-auto">
                                                       <strong>dann unterbiete</strong>
                                                  </div>
                                                  <div className="col-auto">
                                                       <Select
                                                            className="w-100"
                                                            style={{ minWidth: '200px' }}
                                                            options={[
                                                                 {
                                                                      value: "PRICE_WITHOUT_SHIPPING",
                                                                      label: "Preis (ohne Versand)"
                                                                 },
                                                                 {
                                                                      value: "TOTAL_PRICE_WITH_SHIPPING", 
                                                                      label: "Gesamtpreis (mit Versand)"
                                                                 }
                                                            ]}
                                                            value={formula.priceType ? {
                                                                 value: formula.priceType,
                                                                 label: formula.priceType === "PRICE_WITHOUT_SHIPPING" ? "Preis (ohne Versand)" : "Gesamtpreis (mit Versand)"
                                                            } : null}
                                                            onChange={(selectedOption) => {
                                                                 const formulas = pc.getAction();
                                                                 const newFormulas = [...formulas];
                                                                 newFormulas[index] = { 
                                                                      ...newFormulas[index], 
                                                                      priceType: selectedOption?.value || null 
                                                                 };
                                                                 pc.setAction(newFormulas);
                                                            }}
                                                            placeholder="Preistyp wählen..."
                                                       />
                                                  </div>
                                                  <div className="col-auto">
                                                       <strong>um</strong>
                                                  </div>
                                                  <div className="col-auto">
                                                       <PPDecimalInput
                                                            className="form-control"
                                                            style={{ width: '120px' }}
                                                            value={formula.underBidAmount ?? ''}
                                                            onValueChange={(value) => {
                                                                 const formulas = pc.getAction();
                                                                 const newFormulas = [...formulas];
                                                                 newFormulas[index] = { ...newFormulas[index], underBidAmount: parseDecimalInputOrNull(value) };
                                                                 pc.setAction(newFormulas);
                                                            }}
                                                            decimalsLimit={pc.getTypeOfPriceChangeInEuro() ? 2 : 0}
                                                            suffix={pc.getTypeOfPriceChangeInEuro() ? "€" : "%"}
                                                            placeholder={pc.getTypeOfPriceChangeInEuro() ? "0,00" : "0"}
                                                       />
                                                  </div>
                                                  <div className="col-auto">
                                                       <button 
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => {
                                                                 const formulas = pc.getAction();
                                                                 if (formulas.length > 1) {
                                                                      const newFormulas = formulas.filter((_, i) => i !== index);
                                                                      pc.setAction(newFormulas);
                                                                 }
                                                            }}
                                                            disabled={pc.getAction().length <= 1}
                                                       >
                                                            <i className="fas fa-trash"></i>
                                                       </button>
                                                  </div>
                                             </div>
                                             {formula.condition && (
                                                  <div className="row mt-2">
                                                       <div className="col">
                                                            <div className="bg-info p-2 rounded">
                                                                 <small className="text-white">
                                                                      <strong>Bedingung:</strong> {formula.condition.description || 'Bedingung definiert'}
                                                                 </small>
                                                            </div>
                                                       </div>
                                                  </div>
                                             )}
                                        </div>
                                   ))}

                                   <button 
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => {
                                             const formulas = pc.getAction();
                                             pc.setAction([...formulas, { 
                                                  priceType: null, 
                                                  underBidAmount: null, 
                                                  condition: null 
                                             }]);
                                        }}
                                   >
                                        <i className="fas fa-plus me-2"></i>{' '}Weitere Formel hinzufügen
                                   </button>
                              </div>
                         </div>
                    ) : (
                         <>
                              <div className="mb-4">
                                   <h6 className="mb-3">Vergleichspreis:</h6>
                                   <Select
                                        className="w-100"
                                        options={[
                                             {
                                                  value: "CHEAPEST_FS_PREIS",
                                                  label: "günstigster Preis",
                                                  formulaText: "günstigsten Preis"
                                             },
                                             {
                                                  value: "SECOND_CHEAPEST_PRICE",
                                                  label: "zweitgünstigster Preis", 
                                                  formulaText: "zweitgünstigsten Preis"
                                             },
                                             {
                                                  value: "THIRD_CHEAPEST_PRICE",
                                                  label: "drittgünstigster Preis",
                                                  formulaText: "drittgünstigsten Preis"
                                             },
                                             {
                                                  value: "FOURTH_CHEAPEST_PRICE",
                                                  label: "viertgünstigster Preis",
                                                  formulaText: "viertgünstigsten Preis"
                                             },
                                             {
                                                  value: "FIFTH_CHEAPEST_PRICE",
                                                  label: "fünftgünstigster Preis",
                                                  formulaText: "fünftgünstigsten Preis"
                                             },
                                             {
                                                  value: "MOST_EXPENSIVE",
                                                  label: "teuerster Preis",
                                                  formulaText: "teuersten Preis"
                                             },
                                             {
                                                  value: "AVERAGE_PRICE",
                                                  label: "Durchschnittspreis",
                                                  formulaText: "Durchschnittspreis"
                                             },
                                             {
                                                  value: "MEDIAN_PRICE",
                                                  label: "Median",
                                                  formulaText: "Median"
                                             },
                                             {
                                                  value: "NEXT_PRICE_ABOVE_MIN_PRICE",
                                                  label: "nächster Preis oberhalb des Mindestpreis (Exkl. Preisangleichungen)",
                                                  formulaText: "nächsten Preis oberhalb des Mindestpreis (Exkl. Preisangleichungen)"
                                             },
                                             {
                                                  value: "NEXT_PRICE_ABOVE_MIN_PRICE_INCLUSIVE_UNDERCUTTING_VALUE",
                                                  label: "nächster Preis oberhalb des Mindestpreis (Inkl. Preisangleichungen)",
                                                  formulaText: "nächsten Preis oberhalb des Mindestpreis (Inkl. Preisangleichungen)"
                                             }
                                        ]}
                                        value={pc.getComparisonOption()}
                                        onChange={(selectedOption) => pc.setComparisonOption(selectedOption)}
                                        placeholder="Vergleichspreis auswählen..."
                                        isClearable={false}
                                   />
                              </div>

                              {foreignShopStockRules.length > 0 && activeRuleId && (
                                   <div className="mb-4">
                                        <h6 className="mb-3">Verknüpfung Fremdshop-Verfügbarkeit</h6>
                                        <Select
                                             className="w-100"
                                             options={foreignShopStockRules.map((rule) => ({
                                                  value: rule.id,
                                                  label: `Fremdshop Verfügbarkeit: ${(rule.stock || [])
                                                       .map((active, index) => (active ? ["rot", "grün", "gelb"][index] : null))
                                                       .filter(Boolean)
                                                       .join(", ") || "Alle"}`
                                             }))}
                                             value={(() => {
                                                  if (!activeRule?.foreignShopStockRuleReference) return null;
                                                  const linked = foreignShopStockRules.find((r) => r.id === activeRule.foreignShopStockRuleReference);
                                                  if (!linked) return null;
                                                  return {
                                                       value: linked.id,
                                                       label: `Fremdshop Verfügbarkeit: ${(linked.stock || [])
                                                            .map((active, index) => (active ? ["rot", "grün", "gelb"][index] : null))
                                                            .filter(Boolean)
                                                            .join(", ") || "Alle"}`
                                                  };
                                             })()}
                                             onChange={(selected) => {
                                                  PriceChangeHelper.updatePriceAdjustmentRule(
                                                       activeRuleId,
                                                       "foreignShopStockRuleReference",
                                                       selected?.value || null
                                                  );
                                             }}
                                             placeholder="Fremdshop-Verfügbarkeits-Regel wählen..."
                                             isClearable={false}
                                        />
                                   </div>
                              )}

                              {pc.getComparisonOption() && (
                                   <div className="mb-4">
                                        <h6 className="mb-3">Preisformel:</h6>
                                        <div className="bg-light p-3 rounded">
                                             <p className="mb-3">
                                                  <strong>Unser Preis = {pc.getComparisonOption()?.formulaText || "Vergleichspreis"}</strong>
                                             </p>
                                             
                                             {pc.getAction().map((formula, index) => (
                                                  <div key={formulaRowKey(formula)} className="mb-3 p-3 border rounded">
                                                       <div className="row align-items-center">
                                                            <div className="col-auto">
                                                                 <strong>Wenn {pc.getComparisonOption()?.formulaText || "Vergleichspreis"} &le;</strong>
                                                            </div>
                                                            <div className="col-auto">
                                                                 <PPDecimalInput
                                                                      className="form-control"
                                                                      style={{ width: '120px' }}
                                                                      value={formula.priceUntil ?? ''}
                                                                      onValueChange={(value) => {
                                                                           const formulas = pc.getAction();
                                                                           const newFormulas = [...formulas];
                                                                           newFormulas[index] = { ...newFormulas[index], priceUntil: parseDecimalInputOrNull(value) };
                                                                           pc.setAction(newFormulas);
                                                                      }}
                                                                      decimalsLimit={2}
                                                                      suffix="€"
                                                                      placeholder="0,00"
                                                                 />
                                                            </div>
                                                            <div className="col-auto">
                                                                 <strong>dann unterbiete um</strong>
                                                            </div>
                                                            <div className="col-auto">
                                                                 <PPDecimalInput
                                                                      className="form-control"
                                                                      style={{ width: '120px' }}
                                                                      value={formula.underBidAmount ?? ''}
                                                                      onValueChange={(value) => {
                                                                           const formulas = pc.getAction();
                                                                           const newFormulas = [...formulas];
                                                                           newFormulas[index] = { ...newFormulas[index], underBidAmount: parseDecimalInputOrNull(value) };
                                                                           pc.setAction(newFormulas);
                                                                      }}
                                                                      decimalsLimit={pc.getTypeOfPriceChangeInEuro() ? 2 : 0}
                                                                      suffix={pc.getTypeOfPriceChangeInEuro() ? "€" : "%"}
                                                                      placeholder={pc.getTypeOfPriceChangeInEuro() ? "0,00" : "0"}
                                                                 />
                                                            </div>
                                                            <div className="col-auto">
                                                                 <button 
                                                                      className="btn btn-sm btn-danger"
                                                                      onClick={() => {
                                                                           const formulas = pc.getAction();
                                                                           if (formulas.length > 1) {
                                                                                const newFormulas = formulas.filter((_, i) => i !== index);
                                                                                pc.setAction(newFormulas);
                                                                           }
                                                                      }}
                                                                      disabled={pc.getAction().length <= 1}
                                                                 >
                                                                      <i className="fas fa-trash"></i>
                                                                 </button>
                                                            </div>
                                                       </div>
                                                  </div>
                                             ))}

                                             <button 
                                                  className="btn btn-sm btn-secondary"
                                                  onClick={() => {
                                                       const formulas = pc.getAction();
                                                       pc.setAction([...formulas, { priceUntil: null, underBidAmount: null }]);
                                                  }}
                                             >
                                                  <i className="fas fa-plus me-2"></i>{' '}Weitere Formel hinzufügen
                                             </button>
                                        </div>
                                   </div>
                              )}
                         </>
                    )}
               </div>
               
               <PPPriceManagementConditionModal
                    visible={conditionModalVisible}
                    close={() => {
                         setConditionModalVisible(false);
                         setCurrentEditingFormulaIndex(null);
                    }}
                    onConditionSave={handleConditionSave}
                    initialCondition={getCurrentCondition()}
               />
          </div>
     );
}

function PPPriceChangeIntervalModal({ setSaveAction, setCanSave}) {
     const { ChangeIntervalHelper } = useActionRule();

     useEffect(() => {
          setSaveAction(() => () => {
               ChangeIntervalHelper.toggle(true);
          });

          setCanSave(ChangeIntervalHelper.isConfigured());
     }, [setSaveAction, ChangeIntervalHelper, setCanSave]);

     const parseDates = (datesString) => {
          if (!datesString) return { first: "", last: "" };
          const dates = datesString.split(",");
          return {
               first: dates[0] || "",
               last: dates[1] || ""
          };
     };

     const combineDates = (first, last) => {
          return `${first || ""},${last || ""}`;
     };

     const currentDates = parseDates(ChangeIntervalHelper.getDates());
     const currentTurnus = ChangeIntervalHelper.getTimePeriodTurnus();
     const selectedWeekdays = ChangeIntervalHelper.getWeekdays();
     const selectedMonthdays = ChangeIntervalHelper.getMonthdays();

     const parseDateValue = (value) => {
          if (!value) return null;
          const parts = String(value).split("-");
          if (parts.length === 3) {
               const [year, month, day] = parts.map(Number);
               if (!year || !month || !day) return null;
               return new Date(year, month - 1, day);
          }
          const parsed = new Date(value);
          if (Number.isNaN(parsed.getTime())) return null;
          parsed.setHours(0, 0, 0, 0);
          return parsed;
     };

     const toHtmlDate = (date) => {
          if (!date) return "";
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
     };

     const { locale: calendarLocale, dateFormat: calendarDateFormat } = getPrimeCalendarConfig("de");

     const nextExecutionDate = computeNextExecutionDate(
          currentTurnus,
          parseDateValue(currentDates.first),
          parseDateValue(currentDates.last),
          selectedWeekdays,
          selectedMonthdays
     );

     const weekdayOptions = [
          { value: "MONTAG", label: "Montag" },
          { value: "DIENSTAG", label: "Dienstag" },
          { value: "MITTWOCH", label: "Mittwoch" },
          { value: "DONNERSTAG", label: "Donnerstag" },
          { value: "FREITAG", label: "Freitag" },
          { value: "SAMSTAG", label: "Samstag" },
          { value: "SONNTAG", label: "Sonntag" }
     ];

     const monthdayOptions = Array.from({ length: 31 }, (_, i) => ({
          value: (i + 1).toString(),
          label: (i + 1).toString()
     }));

     return (
          <div className="pt-3 pp-form-stack">
               <div className="ps-2 pp-form-inline">
                    <div className="d-flex flex-column">
                         <label className="form-label">
                              Erste Ausführung:
                              <Calendar
                                   value={parseDateValue(currentDates.first)}
                                   onChange={(e) => {
                                        ChangeIntervalHelper.setDates(combineDates(toHtmlDate(e.value), currentDates.last));
                                   }}
                                   dateFormat={calendarDateFormat}
                                   locale={calendarLocale}
                                   showIcon
                                   showButtonBar
                                   showTime={false}
                                   className="w-100"
                                   inputId="change-interval-first-run"
                              />
                         </label>
                    </div>
                    
                    <div className="d-flex flex-column">
                         <label className="form-label">
                              Letzte Ausführung:
                              <Calendar
                                   value={parseDateValue(currentDates.last)}
                                   onChange={(e) => {
                                        ChangeIntervalHelper.setDates(combineDates(currentDates.first, toHtmlDate(e.value)));
                                   }}
                                   dateFormat={calendarDateFormat}
                                   locale={calendarLocale}
                                   showIcon
                                   showButtonBar
                                   showTime={false}
                                   className="w-100"
                                   inputId="change-interval-last-run"
                              />
                         </label>
                    </div>
               </div>

               <div className="ps-2 pp-form-inline">
                    <div className="d-flex flex-column">
                         <p>Turnus:</p>
                    </div>

                    <div className="d-flex flex-column">
                         <label className="form-check">
                              <input 
                                   className="form-check-input" 
                                   type="radio"
                                   name="timePeriodTurnus"
                                   checked={currentTurnus === "WOECHENTLICH"}
                                   onChange={() => ChangeIntervalHelper.setTimePeriodTurnus("WOECHENTLICH")}     
                              />
                         <span className="form-check-label text-md ms-1">Wöchentlich</span>
                    </label>
                         <label className="form-check">
                         <input 
                                   className="form-check-input" 
                                   type="radio"
                                   name="timePeriodTurnus"
                                   checked={currentTurnus === "MONATLICH"}
                                   onChange={() => ChangeIntervalHelper.setTimePeriodTurnus("MONATLICH")}     
                              />
                         <span className="form-check-label text-md ms-1">Monatlich</span>
                    </label>
                         <label className="form-check">
                         <input 
                                   className="form-check-input" 
                                   type="radio"
                                   name="timePeriodTurnus"
                                   checked={currentTurnus === "JAEHRLICH"}
                                   onChange={() => ChangeIntervalHelper.setTimePeriodTurnus("JAEHRLICH")}     
                              />
                         <span className="form-check-label text-md ms-1">Jährlich</span>
                    </label>
                    </div>
               </div>

               {currentTurnus === "WOECHENTLICH" && (
                    <div className="ps-2 pp-form-inline">
                         <span className="text-nowrap">Wochentage:</span>
                         <Select
                              isMulti
                              className="flex-grow-1"
                              options={weekdayOptions}
                              value={weekdayOptions.filter(option => 
                                   ChangeIntervalHelper.getWeekdays().includes(option.value)
                              )}
                              onChange={(selectedOptions) => {
                                   const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                   ChangeIntervalHelper.setWeekdays(values);
                              }}
                              placeholder="Wochentage auswählen..."
                         />
                    </div>
               )}

               {(currentTurnus === "MONATLICH" || currentTurnus === "JAEHRLICH") && (
                    <>
                         <div className="ps-2 pp-form-inline">
                              <span className="text-nowrap">Wochentage:</span>
                              <Select
                                   isMulti
                                   className="flex-grow-1"
                                   options={weekdayOptions}
                                   value={weekdayOptions.filter(option => 
                                        ChangeIntervalHelper.getWeekdays().includes(option.value)
                                   )}
                                   onChange={(selectedOptions) => {
                                        const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                        ChangeIntervalHelper.setWeekdays(values);
                                   }}
                                   placeholder="Wochentage auswählen..."
                              />
                         </div>
                         
                         <div className="ps-2 pp-form-inline">
                              <span className="text-nowrap">Monatstage:</span>
                              <Select
                                   isMulti
                                   className="flex-grow-1"
                                   options={monthdayOptions}
                                   value={monthdayOptions.filter(option => 
                                        ChangeIntervalHelper.getMonthdays().includes(option.value)
                                   )}
                                   onChange={(selectedOptions) => {
                                        const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                        ChangeIntervalHelper.setMonthdays(values);
                                   }}
                                   placeholder="Monatstage auswählen..."
                              />
                         </div>
                    </>
               )}

               {nextExecutionDate && (
                    <div className="ps-2">
                         <div className="alert alert-info mb-0 py-2">
                              Nächste Ausführung: <strong>{nextExecutionDate.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}</strong>
                         </div>
                    </div>
               )}
          </div>
     );
}

function PPPriceRoundingModal({ setSaveAction, setCanSave }) {
     const { RoundPriceHelper } = useActionRule();
     
     const hasValue = (val) =>
          val !== null &&
          val !== undefined &&
          !(typeof val === "string" && val.trim() === "");

     useEffect(() => {
          setSaveAction(() => () => {
               RoundPriceHelper.toggle(true);
          });
     }, [setSaveAction, RoundPriceHelper]);
     
     const currentRoundingValues = RoundPriceHelper.getRoundingValues() || {};
     
     if (!currentRoundingValues.formulas || !Array.isArray(currentRoundingValues.formulas) || currentRoundingValues.formulas.length === 0) {
          currentRoundingValues.formulas = [{ bis: null, nachkommastelle: "", rundungAuf: null }];
     }

     const areRoundingFormulasValid = currentRoundingValues.formulas.every((formula) => {
          const roundingValue = Number(formula?.rundungAuf);
          const maxRoundingValue = formula?.nachkommastelle === "ZWEITE" ? 9 : 99;
          return hasValue(formula?.bis) &&
               hasValue(formula?.nachkommastelle) &&
               Number.isInteger(roundingValue) &&
               roundingValue >= 0 &&
               roundingValue <= maxRoundingValue;
     });
     const roundingType = RoundPriceHelper.getRoundingType();

     useEffect(() => {
          const hasRoundingType = hasValue(roundingType);
          setCanSave(hasRoundingType && areRoundingFormulasValid);
     }, [setCanSave, roundingType, areRoundingFormulasValid]);
     
     const updateRoundingValues = (newValues) => {
          RoundPriceHelper.setRoundingValues(newValues);
     };

     const updateFormula = (index, field, value) => {
          const newFormulas = [...currentRoundingValues.formulas];
          newFormulas[index] = {
               ...newFormulas[index],
               [field]: value
          };
          updateRoundingValues({
               ...currentRoundingValues,
               formulas: newFormulas
          });
     };

     const addFormula = () => {
          const newFormulas = [...currentRoundingValues.formulas, { bis: null, nachkommastelle: "", rundungAuf: null }];
          updateRoundingValues({
               ...currentRoundingValues,
               formulas: newFormulas
          });
     };

     const removeFormula = (index) => {
          if (currentRoundingValues.formulas.length > 1) {
               const newFormulas = currentRoundingValues.formulas.filter((_, i) => i !== index);
               updateRoundingValues({
                    ...currentRoundingValues,
                    formulas: newFormulas
               });
          }
     };

     return (
          <div className="pt-3 pp-form-stack">
               <div className="ps-2 pp-form-inline">
                    <div className="d-flex flex-column">
                         <p>Art der Preisrundung:</p>
                    </div>

                    <div className="d-flex flex-column">
                         <label className="form-check">
                         <input 
                                   className="form-check-input" 
                                   type="radio"
                                   name="roundingType"
                                   checked={RoundPriceHelper.getRoundingType() === "IMMER_AUFRUNDEN"}
                                   onChange={() => RoundPriceHelper.setRoundingType("IMMER_AUFRUNDEN")}     
                              />
                         <span className="form-check-label text-md ms-1">Immer aufrunden</span>
                    </label>
                         <label className="form-check">
                         <input 
                                   className="form-check-input" 
                                   type="radio"
                                   name="roundingType"
                                   checked={RoundPriceHelper.getRoundingType() === "IMMER_ABRUNDEN"}
                                   onChange={() => RoundPriceHelper.setRoundingType("IMMER_ABRUNDEN")}     
                              />
                         <span className="form-check-label text-md ms-1">Immer abrunden</span>
                    </label>
                         <label className="form-check">
                         <input 
                                   className="form-check-input" 
                                   type="radio"
                                   name="roundingType"
                                   checked={RoundPriceHelper.getRoundingType() === "AB_HAELFTE_AUF"}
                                   onChange={() => RoundPriceHelper.setRoundingType("AB_HAELFTE_AUF")}     
                              />
                         <span className="form-check-label text-md ms-1">Ab Hälfte auf-, sonst abrunden</span>
                    </label>
                    </div>
               </div>

               <div className="ps-2">
                    <label className="form-check form-switch form-switch-inline">
                         <input 
                              className="form-check-input" 
                              type="checkbox" 
                              role="switch"
                              checked={RoundPriceHelper.getContraryBackupRule()}
                              onChange={() => RoundPriceHelper.setContraryBackupRule(
                                   !RoundPriceHelper.getContraryBackupRule()
                              )}     
                         />
                         <span className="form-check-label text-md ms-1">Gegenoperation bei Backup Regel ausführen</span>
                    </label>
               </div>

               {currentRoundingValues.formulas.map((formula, index) => (
                    <div key={formulaRowKey(formula)} className="d-flex flex-column" style={{ gap: '0.5rem' }}>
                         <div className="input-group input-group-sm d-flex align-items-center" style={{gap: '1rem'}}>
                              <span className="text-nowrap">Bis</span>
                              
                              <PPDecimalInput
                                   value={formula.bis}
                                   onValueChange={(v) => updateFormula(index, 'bis', v)}
                                   className={`form-control`}
                                   decimalsLimit={2}
                                   suffix={"€"}
                                   step={1}
                                   maxLength={6}
                              />

                              <span className="text-nowrap">wird ab der</span>
                              
                              <div className="d-flex flex-column">
                                   <label className="form-check">
                         <input 
                                             className="form-check-input" 
                                             type="radio"
                                             name={`decimalPlace-${index}`}
                                             checked={formula.nachkommastelle === "ERSTE"}
                                             onChange={() => updateFormula(index, 'nachkommastelle', "ERSTE")}     
                                        />
                         <span className="form-check-label text-md ms-1">ersten Nachkommastelle</span>
                    </label>
                                   <label className="form-check">
                         <input 
                                             className="form-check-input" 
                                             type="radio"
                                             name={`decimalPlace-${index}`}
                                             checked={formula.nachkommastelle === "ZWEITE"}
                                             onChange={() => updateFormula(index, 'nachkommastelle', "ZWEITE")}     
                                        />
                         <span className="form-check-label text-md ms-1">zweite Nachkommastelle</span>
                    </label>
                              </div>
                              
                              <span className="text-nowrap">gerundet auf</span>
                              
                              <input
                                   type="number"
                                   className="form-control"
                                   style={{ width: '80px' }}
                                   min="0"
                                   max={formula.nachkommastelle === "ZWEITE" ? "9" : "99"}
                                   value={formula.rundungAuf || ''}
                                   onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        const maxRoundingValue = formula.nachkommastelle === "ZWEITE" ? 9 : 99;
                                        if (value >= 0 && value <= maxRoundingValue) {
                                             updateFormula(index, 'rundungAuf', value);
                                        }
                                   }}
                              />

                              {index === currentRoundingValues.formulas.length - 1 && (
                                   <button 
                                        className="btn btn-success btn-sm"
                                        onClick={addFormula}
                                        style={{ minWidth: '30px' }}
                                   >
                                        +
                                   </button>
                              )}

                              {currentRoundingValues.formulas.length > 1 && (
                                   <button 
                                        className="btn btn-danger btn-sm"
                                        onClick={() => removeFormula(index)}
                                        style={{ minWidth: '30px' }}
                                   >
                                        x
                                   </button>
                              )}
                         </div>
                    </div>
               ))}
          </div>
     );
}

function formulaOpToSelect(op) {
     if (!op) return null
     const symbols = { DIVISION: "/", MULTIPLICATION: "*", PLUS: "+", MINUS: "-" }
     const symbol = symbols[op] || op
     return { value: symbol, label: symbol }
}

function formulaToFormularState(currentFormula) {
     return {
          OP_1: formulaOpToSelect(currentFormula.OP_1),
          X: currentFormula.X || 0,
          OP_2: formulaOpToSelect(currentFormula.OP_2),
          Y: currentFormula.Y || 0,
          OP_3: formulaOpToSelect(currentFormula.OP_3),
          Z: currentFormula.Z || 0,
          useFactor: currentFormula.useFactor || false
     };
}

function PPPriceChangeViaMarginModal({ editingRule, setSaveAction, setCanSave }) {
     const { PriceChangeMarginHelper } = useActionRule();
     const { PriceTrackingHelper } = useForeignShopRule();
     const { getActionRuleOptions } = useOptions();
     
     const actionRuleOptions = getActionRuleOptions();
     const numericKeysOptions = actionRuleOptions.productAttributeNumericKeys

     const currentFormula = PriceChangeMarginHelper.getFormula();
     const [formular, setFormular] = useState(() => formulaToFormularState(currentFormula));

     useEffect(() => {
          setFormular(formulaToFormularState(PriceChangeMarginHelper.getFormula()));
     }, [editingRule]);

     const margeFall = PriceChangeMarginHelper.getMargeFall();
     const margeType = PriceChangeMarginHelper.getMargeType();
     const minimumBoundaryEnabled = PriceChangeMarginHelper.isMinimumBoundaryEnabled();
     const isTooler = PPUtils.getCustomerName() === "Tooler";

     useEffect(() => {
          setSaveAction(() => () => {
               if (PriceTrackingHelper.isActive()) {
                    alert("Preisanpassung nach Marge kann nicht aktiviert werden, da bereits Preistracking Mitbewerb aktiv ist.");
                    return;
               }

               PriceChangeMarginHelper.setFormula({
                    OP_1: formular.OP_1?.value ? convertToServerOperator(formular.OP_1.value) : null,
                    X: finiteNumberOrZero(formular.X),
                    OP_2: formular.OP_2?.value ? convertToServerOperator(formular.OP_2.value) : null,
                    Y: finiteNumberOrZero(formular.Y),
                    OP_3: formular.OP_3?.value ? convertToServerOperator(formular.OP_3.value) : null,
                    Z: finiteNumberOrZero(formular.Z),
                    useFactor: formular.useFactor || false
               });
               
               PriceChangeMarginHelper.toggle(true);
          });

          setCanSave(PriceChangeMarginHelper.isConfigured());
     }, [setSaveAction, PriceChangeMarginHelper, PriceTrackingHelper, formular, setCanSave, margeFall, margeType]);
     
     return (
          <div className="pt-3 pp-form-stack">
               <div className="ps-2 pp-form-inline">
                    <div className="d-flex flex-column">
                         <p>Anwendungsfall:</p>
                    </div>

                    <div className="d-flex flex-column">
                         <label className="form-check form-switch form-switch-inline">
                         <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={margeFall === "ALL_PRODUCTS"}
                                   onChange={() => PriceChangeMarginHelper.setMargeFall("ALL_PRODUCTS")}     
                              />
                         <span className="form-check-label text-md ms-1">Auf alle Produkte</span>
                    </label>
                         <label className="form-check form-switch form-switch-inline">
                         <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={margeFall === "ONLY_LESSER"}
                                   onChange={() => PriceChangeMarginHelper.setMargeFall("ONLY_LESSER")}     
                              />
                         <span className="form-check-label text-md ms-1">Nur, wenn aktuelle Marge kleiner</span>
                    </label>
                         <label className="form-check form-switch form-switch-inline">
                         <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={margeFall === "ONLY_GREATER"}
                                   onChange={() => PriceChangeMarginHelper.setMargeFall("ONLY_GREATER")}     
                              />
                         <span className="form-check-label text-md ms-1">Nur, wenn aktuelle Marge größer</span>
                    </label>
                    </div>
               </div>

               <div className="ps-2 pp-form-inline">
                    <div className="d-flex flex-column">
                         <p>Preisanpassung durch:</p>
                    </div>

                    <div className="d-flex flex-column">
                         <label className="form-check form-switch form-switch-inline">
                         <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={margeType === "FORMEL"}
                                   onChange={() => PriceChangeMarginHelper.setMargeType("FORMEL")}     
                              />
                         <span className="form-check-label text-md ms-1">Formel</span>
                    </label>
                         <label className="form-check form-switch form-switch-inline">
                         <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={margeType === "NETTOFAKTOR"}
                                   onChange={() => PriceChangeMarginHelper.setMargeType("NETTOFAKTOR")}     
                              />
                         <span className="form-check-label text-md ms-1">Nettofaktor</span>
                    </label>
                         <label className="form-check form-switch form-switch-inline">
                         <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={margeType === "BRUTTOFAKTOR"}
                                   onChange={() => PriceChangeMarginHelper.setMargeType("BRUTTOFAKTOR")}     
                              />
                         <span className="form-check-label text-md ms-1">Bruttofaktor</span>
                    </label>
                    </div>
               </div>

               {(margeType === "NETTOFAKTOR" || margeType === "BRUTTOFAKTOR") && (
                    <div className="input-group input-group-sm d-flex align-items-center flex-wrap" style={{gap: '1rem'}}>
                         <span className="mx-2 text-nowrap">
                              {margeType === "NETTOFAKTOR" ? "Nettofaktor:" : "Bruttofaktor:"}
                         </span>
                         <PPDecimalInput
                              value={PriceChangeMarginHelper.getNettoBruttoValue()}
                              onValueChange={(v) => PriceChangeMarginHelper.setNettoBruttoValue(v)}
                              className="form-control"
                              decimalsLimit={4}
                              step={0.01}
                              maxLength={8}
                         />
                    </div>
               )}

               {margeType === "FORMEL" && (
                    <>
                         <div className="input-group input-group-sm d-flex align-items-center flex-wrap" style={{gap: '1rem'}}>
                              <span className="mx-2 text-nowrap">Basispreis für Formel:</span>

                              <PPDefaultSelector 
                                   id="selectActionRule"
                                   data={numericKeysOptions}
                                   onChange={(e) => PriceChangeMarginHelper.setMinimumPriceFormulaBase(e?.value || "")}
                                   name="Action Select"
                                   value={selectedOption(numericKeysOptions, PriceChangeMarginHelper.getMinimumPriceFormulaBase())}
                              />

                              <PPDefaultSelector 
                                   id="selectActionRuleOP_1"
                                   data={[
                                        { value: "+", label: "+" },
                                        { value: "-", label: "-" },
                                        { value: "*", label: "*" },
                                        { value: "/", label: "/" }
                                   ]}
                                   onChange={(e) => setFormular(prev => ({
                                        ...prev,
                                        "OP_1": e 
                                   }))}
                                   name="Action Select"
                                   value={formular["OP_1"]}
                              />
                              
                              <PPDecimalInput
                                   value={formular["X"]}
                                   onValueChange={(v) => setFormular(prev => ({
                                        ...prev,
                                        "X": v 
                                   }))}
                                   className={`form-control`}
                                   decimalsLimit={2}
                                   suffix={"€"}
                                   step={1}
                                   maxLength={6}
                              />

                              <PPDefaultSelector 
                                   id="selectActionRuleOP_2"
                                   data={[
                                        { value: "+", label: "+" },
                                        { value: "-", label: "-" }
                                   ]}
                                   onChange={(e) => setFormular(prev => ({
                                        ...prev,
                                        "OP_2": e 
                                   }))}
                                   name="Action Select"
                                   value={formular["OP_2"]}
                              />
                              
                              <PPDecimalInput
                                   value={formular["Y"]}
                                   onValueChange={(v) => setFormular(prev => ({
                                        ...prev,
                                        "Y": v 
                                   }))}
                                   className={`form-control`}
                                   decimalsLimit={2}
                                   suffix={"€"}
                                   step={1}
                                   maxLength={3}
                              />

                              <PPDefaultSelector 
                                   id="selectActionRuleOP_3"
                                   data={[
                                        { value: "+", label: "+" },
                                        { value: "-", label: "-" },
                                        { value: "*", label: "*" },
                                        { value: "/", label: "/" }
                                   ]}
                                   onChange={(e) => setFormular(prev => ({
                                        ...prev,
                                        "OP_3": e 
                                   }))}
                                   name="Action Select"
                                   value={formular["OP_3"]}
                              />
                              
                              <PPDecimalInput
                                   value={formular["Z"]}
                                   onValueChange={(v) => setFormular(prev => ({
                                        ...prev,
                                        "Z": v }))}
                                   className={`form-control`}
                                   decimalsLimit={2}
                                   suffix={"%"}
                                   step={1}
                                   maxLength={3}
                              />
                         </div>

                         {isTooler && (
                              <div className="form-check mt-2">
                                   <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="margin_use_feed_factor"
                                        checked={formular.useFactor}
                                        onChange={(e) => setFormular((prev) => ({ ...prev, useFactor: e.target.checked }))}
                                   />
                                   <label className="form-check-label" htmlFor="margin_use_feed_factor">
                                        × Feed Faktor
                                   </label>
                              </div>
                         )}

                         <label className="form-check form-switch form-switch-inline">
                              <input 
                                   className="form-check-input" 
                                   type="checkbox" 
                                   role="switch"
                                   checked={PriceChangeMarginHelper.getMinimumPriceFormulaAddTax()}
                                   onChange={() => PriceChangeMarginHelper.setMinimumPriceFormulaAddTax(
                                        !PriceChangeMarginHelper.getMinimumPriceFormulaAddTax()
                                   )}     
                              />
                         <span className="form-check-label text-md ms-1">Mehrwertsteuer nach der Berechnung der Marge aufschlagen</span>
                    </label>
                    </>
               )}

               <div>
                    <div className="form-check form-switch form-switch-inline mb-2">
                         <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="margin_minimum_boundary_enabled"
                              checked={minimumBoundaryEnabled}
                              onChange={() => PriceChangeMarginHelper.setMinimumBoundaryEnabled(!minimumBoundaryEnabled)}
                         />
                         <label className="form-check-label text-md ms-1" htmlFor="margin_minimum_boundary_enabled">
                              Aber mindestens aktivieren
                         </label>
                    </div>

                    <div
                         className={`input-group input-group-sm d-flex align-items-center flex-wrap ${minimumBoundaryEnabled ? "" : "opacity-50"}`}
                         style={{gap: '1rem'}}
                    >
                    <span className="mx-2 text-nowrap">Aber mindestens: (EK +</span>
                    
                    <PPDecimalInput
                         value={PriceChangeMarginHelper.getMinimumEuroOverEK() ?? 0}
                         onValueChange={(v) => PriceChangeMarginHelper.setMinimumEuroOverEK(v)}
                         className={`form-control`}
                         decimalsLimit={2}
                         suffix={"€"}
                         step={1}
                         maxLength={6}
                         disabled={!minimumBoundaryEnabled}
                    />

                    <span className="mx-2 text-nowrap">) + MwSt.</span>

                    <PPDecimalInput
                         value={PriceChangeMarginHelper.getMinimumPercent() ?? 0}
                         onValueChange={(v) => PriceChangeMarginHelper.setMinimumPercent(v)}
                         className={`form-control`}
                         decimalsLimit={2}
                         suffix={"%"}
                         step={1}
                         maxLength={3}
                         disabled={!minimumBoundaryEnabled}
                    />
                    </div>
               </div>
          </div>
     );
}

function PPChannelRuleCalculationModal({ setSaveAction, setCanSave }) {
     const { CalculationChannelAndRuleHelper } = useActionRule();
     const { LanguageAndChannelHelper } = useProductRule();
     const { getActionRuleOptions } = useOptions();
     const [rules, setRules] = useState([]);
     const [loading, setLoading] = useState(false);

     const ownChannel = LanguageAndChannelHelper?.getChannel?.() || null;
     const ownChannelId = ownChannel?.id ?? null;
     const ownChannelName = ownChannel?.channelName || ownChannel?.value || ownChannel?.label || null;

     useEffect(() => {
          setSaveAction(() => () => {
               CalculationChannelAndRuleHelper.toggle(true);
          });

          setCanSave(CalculationChannelAndRuleHelper.isConfigured());
     }, [setSaveAction, CalculationChannelAndRuleHelper, setCanSave]);
     
     const actionRuleOptions = getActionRuleOptions();
     const channelOptions = actionRuleOptions.calculationChannelAndRulePrice.channel
          .filter((channel) => {
               const isOwnId = ownChannelId != null && String(channel.id) === String(ownChannelId)
               const isOwnName = ownChannelName != null && channel.channelName === ownChannelName
               return !isOwnId && !isOwnName
          })
          .map(channel => ({
               value: channel.id,
               label: channel.channelName
          }));

     const selectedChannelId = CalculationChannelAndRuleHelper.getChannelId();
     const selectedRuleId = CalculationChannelAndRuleHelper.getRuleId();

     useEffect(() => {
          if (selectedChannelId == null) {
               return;
          }
          const selectedIsOwnChannel =
               (ownChannelId != null && String(selectedChannelId) === String(ownChannelId))
               || (ownChannelName != null && actionRuleOptions.calculationChannelAndRulePrice.channel.some(
                    (channel) => String(channel.id) === String(selectedChannelId) && channel.channelName === ownChannelName
               ));
          if (selectedIsOwnChannel) {
               CalculationChannelAndRuleHelper.setChannelId(null);
               CalculationChannelAndRuleHelper.setRuleId(null);
               setRules([]);
               setCanSave(false);
          }
     }, [selectedChannelId, ownChannelId, ownChannelName]);

     const selectedChannelOption = channelOptions.find(o => o.value === selectedChannelId) || null;
     const selectedRuleOption = rules.find(o => o.value === selectedRuleId) || null;

     const fetchRulesByChannel = async (channelId) => {
          if (!channelId) {
               setRules([]);
               return;
          }
          
          setLoading(true);
          try {
               const auth = PPUtils.getLogin();
               const token = auth.token;
               const customerId = PPUtils.getEffectiveCustomerId(auth);
               const rulesResponse = await RuleEngineBackend.getRulesByChannel(channelId, customerId, token);
               
               if (rulesResponse && Array.isArray(rulesResponse)) {
                    const ruleOptions = rulesResponse.map(rule => ({
                         value: rule.id,
                         label: `${rule.name} (${rule.id})`
                    }));
                    setRules(ruleOptions);
               } else {
                    setRules([]);
               }
          } catch (error) {
               console.error('Error fetching rules:', error);
               setRules([]);
          } finally {
               setLoading(false);
          }
     };

     const handleChannelChange = (selectedChannel) => {
          const nextChannelId = selectedChannel?.value ?? null;
          CalculationChannelAndRuleHelper.setChannelId(nextChannelId);
          CalculationChannelAndRuleHelper.setRuleId(null);
          fetchRulesByChannel(nextChannelId);
     };

     useEffect(() => {
          const currentChannelId = CalculationChannelAndRuleHelper.getChannelId();
          if (currentChannelId) {
               fetchRulesByChannel(currentChannelId);
          }
     }, [selectedChannelId]);

     return (
          <div className="pt-3 pp-form-stack">
               <div className="ps-2 pp-form-inline">
                    <span className="text-nowrap">Kanal:</span>
                    <PPDefaultSelector 
                         id="selectChannel"
                         data={channelOptions}
                         onChange={handleChannelChange}
                         name="Channel Select"
                         value={selectedChannelOption}
                    />
               </div>

               <div className="ps-2 pp-form-inline">
                    <span className="text-nowrap">Regel (optional):</span>
                    <PPDefaultSelector 
                         id="selectRule"
                         data={rules}
                         onChange={(selectedRule) => CalculationChannelAndRuleHelper.setRuleId(selectedRule?.value ?? null)}
                         name="Rule Select"
                         value={selectedRuleOption}
                         disabled={loading || rules.length === 0}
                         customStyles={{
                              container: (provided) => ({
                                   ...provided,
                                   minWidth: "320px",
                                   width: "320px",
                              }),
                              control: (provided) => ({
                                   ...provided,
                                   minHeight: "38px",
                              }),
                              valueContainer: (provided) => ({
                                   ...provided,
                                   paddingLeft: "6px",
                                   paddingRight: "6px",
                              }),
                              singleValue: (provided) => ({
                                   ...provided,
                                   whiteSpace: "nowrap",
                              }),
                         }}
                    />
                    {loading && <span className="text-muted">Lade Regeln...</span>}
               </div>

               <div className="ps-2" style={{ maxWidth: '600px' }}>
                    <p className="text-muted small">
                         <strong>Hinweis:</strong> Beim Auswählen einer Regel werden die Regel-Fokus Komponenten
                         der ausgewählten Regel übernommen. Sollten diese Komponenten in der ausgewählten Regel
                         zu einem späteren Zeitpunkt verändert werden, so werden auch die jeweiligen
                         Komponenten aller nachfolgenden Regeln automatisch angepasst.
                    </p>
               </div>
          </div>
     );
}

function PPAmazonBuyBoxModal({ setSaveAction, setCanSave }) {
     const { AmazonBuyBoxHelper } = useActionRule();

     useEffect(() => {
          setSaveAction(() => () => {
               AmazonBuyBoxHelper.toggle(true);
          });
          setCanSave(true);
     }, [setSaveAction, setCanSave, AmazonBuyBoxHelper]);

     return (
          <div className="pt-3 pp-form-stack">
               <div className="alert alert-info mb-0">
                    <p className="mb-2">
                         Wenn Amazon in der Shop-Filterung enthalten ist und für ein Produkt keine BuyBox
                         vorhanden ist, wird der von Amazon empfohlene BuyBox-Preis gesetzt – sofern er
                         den Mindestpreis nicht unterschreitet. Andernfalls wird der Mindestpreis verwendet.
                    </p>
                    <p className="mb-0 small text-white">
                         Liegt bereits eine BuyBox vor, greift die Preisangleichung oder die Preisanpassung nach Marge.
                         Eine dieser Komponenten muss in der Regel vorhanden sein.
                    </p>
               </div>
          </div>
     );
}
